import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { LockIcon, FileTextIcon, GitForkIcon, HandshakeIcon, DollarSignIcon, MapPinIcon, CompassIcon, HandIcon, EyeIcon, ClockIcon, ShieldIcon, ArrowRightIcon } from 'lucide-react';
interface OwnersProps {
  onOwnerContactOpen: () => void;
}
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.15
    }
  },
  viewport: {
    once: true
  }
};
export function Owners({
  onOwnerContactOpen
}: OwnersProps) {
  return <main className="bg-deal-black pt-20" data-id="element-632">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden" data-id="element-633">
        <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-634" />
        <div className="absolute inset-0 bg-gradient-to-b from-deal-black/20 via-transparent to-deal-black" data-id="element-635" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-20 pb-20" data-id="element-636">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-637">
            <span className="inline-block text-deal-green text-[11px] font-semibold tracking-[0.25em] uppercase mb-8 border border-deal-green/30 px-4 py-1.5" data-id="element-638">
              Confidential & No Obligation
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="text-5xl md:text-6xl lg:text-[72px] font-bold text-white leading-[1.05] tracking-tight mb-8" data-id="element-639">
            Thinking About Selling
            <br data-id="element-640" />
            <span className="text-deal-green" data-id="element-641">Your $5M+ Business?</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto mb-12 leading-relaxed font-light" data-id="element-642">
            Explore your options confidentially. Speak with qualified buyers
            without listing your business or committing to a sale. You stay in
            control of the entire process.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-643">
            <button onClick={onOwnerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2 mx-auto" data-id="element-644">
              Start Confidentially
              <ArrowRightIcon className="w-4 h-4" data-id="element-645" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Why This Route Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-646">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-647">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-648">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-649">
              Why This Route
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-650">
              A better way to explore your options.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-651">
              You've built something valuable. Before you decide anything, you
              deserve to understand what's possible — on your terms, with
              complete confidentiality.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06]" data-id="element-652">
            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-653">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-654">
                <LockIcon className="w-5 h-5 text-deal-green" data-id="element-655" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-656">
                Confidential, Controlled Process
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-657">
                Your business is never listed publicly. Your employees,
                customers, and competitors won't know you're exploring. We share
                information only with your explicit consent — and only in
                general, non-identifying terms until you're ready for a direct
                conversation.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-658">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-659">
                <FileTextIcon className="w-5 h-5 text-deal-green" data-id="element-660" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-661">
                No Public Listing Required
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-662">
                Unlike traditional broker processes, there's no listing
                agreement, no marketing package, no "going to market." You're
                simply exploring what a conversation with a qualified buyer
                looks like — without the circus that usually surrounds it.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-663">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-664">
                <GitForkIcon className="w-5 h-5 text-deal-green" data-id="element-665" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-666">
                Optionality First — Clarity Before Commitment
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-667">
                Most owners aren't sure if they want to sell. That's perfectly
                fine. Our process gives you information and options without
                requiring decisions. You might decide to sell. You might decide
                to wait. Either way, you'll know what's possible.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-668">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-669">
                <HandshakeIcon className="w-5 h-5 text-deal-green" data-id="element-670" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-671">
                Flexible Deal Structures
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-672">
                Full exit, majority sale, minority investment, transition
                periods — the buyers in our network are experienced and
                flexible. Whatever structure works best for your goals, your
                family, and your legacy — that's where the conversation starts.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What Happens Next Section */}
      <section className="py-28 md:py-36 bg-deal-black border-t border-white/[0.06]" data-id="element-673">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-674">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-675">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-676">
              What Happens Next
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-677">
              Three simple steps. You're in control at every one.
            </h2>
          </motion.div>

          <div className="space-y-16" data-id="element-678">
            <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-679">
              <div className="md:col-span-1" data-id="element-680">
                <div className="w-12 h-12 rounded-full border border-deal-green/30 flex items-center justify-center text-deal-green text-lg font-bold" data-id="element-681">
                  01
                </div>
              </div>
              <div className="md:col-span-11" data-id="element-682">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-683">
                  Submit a Short Overview
                </h3>
                <p className="text-white/50 leading-relaxed" data-id="element-684">
                  Tell us the basics: revenue range, profit range, industry,
                  location, and why you're exploring. Takes about 3 minutes.
                  Everything is confidential from the start.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-685">
              <div className="md:col-span-1" data-id="element-686">
                <div className="w-12 h-12 rounded-full border border-deal-green/30 flex items-center justify-center text-deal-green text-lg font-bold" data-id="element-687">
                  02
                </div>
              </div>
              <div className="md:col-span-11" data-id="element-688">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-689">
                  Intro Call to Understand Goals
                </h3>
                <p className="text-white/50 leading-relaxed" data-id="element-690">
                  We'll have a relaxed, confidential conversation about your
                  goals, your business, and what you're looking for. No
                  pressure. No hard sell. Just a genuine conversation about
                  what's possible.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-691">
              <div className="md:col-span-1" data-id="element-692">
                <div className="w-12 h-12 rounded-full border border-deal-green/30 flex items-center justify-center text-deal-green text-lg font-bold" data-id="element-693">
                  03
                </div>
              </div>
              <div className="md:col-span-11" data-id="element-694">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4" data-id="element-695">
                  We Coordinate Qualified Conversations
                </h3>
                <p className="text-white/50 leading-relaxed" data-id="element-696">
                  If there's a fit, we confidentially introduce you to qualified
                  buyers who match your criteria. You decide who to speak with,
                  when, and how. No one contacts you without your permission.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Ask For Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d]" data-id="element-697">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-698">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-699">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-700">
              What We Ask For
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mb-6" data-id="element-701">
              Just the basics. Nothing invasive.
            </h2>
            <p className="text-white/50 text-lg leading-relaxed font-light max-w-2xl" data-id="element-702">
              We intentionally keep the initial information request simple. We
              never ask for detailed financials, customer lists, or sensitive
              data upfront.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06]" data-id="element-703">
            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-704">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-705">
                <DollarSignIcon className="w-5 h-5 text-deal-green" data-id="element-706" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-707">
                Revenue & Profit Range
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-708">
                Approximate ranges — not audited statements
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-709">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-710">
                <MapPinIcon className="w-5 h-5 text-deal-green" data-id="element-711" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-712">
                Location & Industry
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-713">
                Where you operate and what you do
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-714">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-715">
                <CompassIcon className="w-5 h-5 text-deal-green" data-id="element-716" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-717">Why & When</h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-718">
                Your motivation and approximate timeline
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Your Control Section */}
      <section className="py-28 md:py-36 bg-deal-black border-t border-white/[0.06]" data-id="element-719">
        <div className="max-w-6xl mx-auto px-6 lg:px-10" data-id="element-720">
          <motion.div {...fadeInUp} className="mb-16" data-id="element-721">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-722">
              Your Control
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl" data-id="element-723">
              No pressure. No obligation. Ever.
            </h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06]" data-id="element-724">
            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-725">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-726">
                <HandIcon className="w-5 h-5 text-deal-green" data-id="element-727" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-728">
                You Decide If You Proceed
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-729">
                At every step, the choice is yours. If you decide the timing
                isn't right, you simply step back. No questions, no follow-up
                pressure, no guilt.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-730">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-731">
                <EyeIcon className="w-5 h-5 text-deal-green" data-id="element-732" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-733">
                You Choose Who You Speak With
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-734">
                We never connect you with a buyer without your explicit
                approval. You see who they are, what they're looking for, and
                why they might be a good fit — before any conversation happens.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-735">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-736">
                <ClockIcon className="w-5 h-5 text-deal-green" data-id="element-737" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-738">
                You Set the Pace
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-739">
                Want to move quickly? We can coordinate conversations within
                days. Want to take your time? No problem. This process runs on
                your timeline, not anyone else's.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-[#0d0d0d] p-10 md:p-12 group" data-id="element-740">
              <div className="w-10 h-10 bg-deal-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-deal-green/20 transition-colors" data-id="element-741">
                <ShieldIcon className="w-5 h-5 text-deal-green" data-id="element-742" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-743">
                There Is No Cost to You
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-744">
                Our service is paid for by the buyer side. You will never
                receive a bill, owe a fee, or face any financial obligation for
                exploring your options through our process. The only investment
                we ask is your time — and we respect it.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 md:py-36 bg-[#0d0d0d] relative overflow-hidden" data-id="element-745">
        <div className="absolute inset-0 bg-gradient-to-t from-deal-green/[0.04] to-transparent" data-id="element-746" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 text-center" data-id="element-747">
          <motion.div {...fadeInUp} data-id="element-748">
            <p className="text-[11px] text-deal-green tracking-[0.25em] uppercase font-semibold mb-6" data-id="element-749">
              Start Here
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-750">
              Begin a confidential conversation.
            </h2>
            <p className="text-lg text-white/45 mb-10 max-w-2xl mx-auto font-light" data-id="element-751">
              Everything you share is confidential. We'll follow up within 1
              business day to schedule a no-obligation intro call.
            </p>
            <button onClick={onOwnerContactOpen} className="px-8 py-4 bg-deal-green text-black text-sm font-bold tracking-widest uppercase hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2 mx-auto" data-id="element-752">
              Start Confidentially
              <ArrowRightIcon className="w-4 h-4" data-id="element-753" />
            </button>
          </motion.div>
        </div>
      </section>
    </main>;
}